import { useCallback, useEffect, useMemo, useRef } from 'react';
import { usePreference } from '../../hooks/use-preference';
import { evaluateStrategy, incrementClickCount, incrementDismissCount } from './evaluators';
import { FeatureId, EligibilityCriteria, EligibilityData } from './types';

type UseEligibilityOptions = {
  featureId: FeatureId;
  defaultCriteria: EligibilityCriteria;
};

export const useEligibility = ({ featureId, defaultCriteria }: UseEligibilityOptions) => {
  // omitDefaultValue: true so we can distinguish between "not yet loaded" (undefined) and "loaded but empty" (null)
  // We use null as our sentinel value in DEFAULTS.ts to indicate "loaded but no data set yet"
  const [eligibility, setEligibility] = usePreference('Eligibility');
  const hasInitialized = useRef(false);
  const isEligibilityHydrating = eligibility === undefined;

  // O(1) lookup by featureId
  const currentFeatureEligibilityData = useMemo(() => {
    return eligibility?.[featureId];
  }, [eligibility, featureId]);

  // Check if criteria structure has changed (developer updated the default)
  const criteriaStructureChanged = useMemo(() => {
    return hasCriteriaStructureChanged(currentFeatureEligibilityData?.criteria, defaultCriteria);
  }, [currentFeatureEligibilityData?.criteria, defaultCriteria]);

  // Helper to update eligibility data for this feature (update eligibility in user preferences for this featureId)
  const updateEligibilityData = useCallback(
    (updates: Partial<EligibilityData>) => {
      const updatedEntry: EligibilityData = {
        criteria: currentFeatureEligibilityData?.criteria ?? defaultCriteria,
        ...updates,
      };

      setEligibility({
        ...(eligibility ?? {}),
        [featureId]: updatedEntry,
      });
    },
    [featureId, eligibility, currentFeatureEligibilityData, defaultCriteria, setEligibility]
  );

  // INITIALIZATION & AUTO-INCREMENT EFFECT
  // This runs once on mount to:
  // 1. Initialize data if it doesn't exist yet
  // 2. Apply auto-increments (days-shown on new day, view-count on each view)
  // 3. Reset criteria if the structure has changed (developer updated defaultCriteria)
  useEffect(() => {
    if (hasInitialized.current) return;

    // eligibility is undefined only when the preference store hasn't hydrated yet.
    // Once hydrated, it will be at least {} (the default value from DEFAULTS.ts).
    if (isEligibilityHydrating) return;

    hasInitialized.current = true;

    // If criteria structure changed or there is no existing eligibility data for this feature, initialize with default
    // criteria (this handles both first-time initialization and developer updates to criteria)
    if (criteriaStructureChanged || !currentFeatureEligibilityData) {
      const { updatedCriteria } = evaluateStrategy(defaultCriteria);
      updateEligibilityData({
        criteria: updatedCriteria ?? defaultCriteria,
      });
      return;
    }

    // Evaluate existing criteria to check if any auto-increments are needed (e.g., increment days-shown if last seen
    // was a different day, increment view-count on each view)
    const { updatedCriteria } = evaluateStrategy(currentFeatureEligibilityData.criteria);
    if (updatedCriteria) {
      updateEligibilityData({ criteria: updatedCriteria });
    }
  }, [
    eligibility,
    currentFeatureEligibilityData,
    defaultCriteria,
    updateEligibilityData,
    criteriaStructureChanged,
    isEligibilityHydrating,
  ]);

  // Determine if the element should be shown (read-only, no side effects - will not update counts or criteria)
  const shouldShow = useMemo(() => {
    if (isEligibilityHydrating) {
      return false;
    }
    // If this feature is explicitly disabled for this user, don't show
    if (currentFeatureEligibilityData?.disable) {
      return false;
    }

    // If criteria structure changed, use default criteria for evaluation
    const criteria = criteriaStructureChanged
      ? defaultCriteria
      : currentFeatureEligibilityData?.criteria ?? defaultCriteria;

    const { shouldShow: evaluatedShouldShow } = evaluateStrategy(criteria);
    return evaluatedShouldShow;
  }, [currentFeatureEligibilityData, defaultCriteria, criteriaStructureChanged, isEligibilityHydrating]);

  // Explicit action: disable eligibility for this featureId (e.g., user clicks "Don't show again")
  const disableEligibility = useCallback(() => {
    updateEligibilityData({ disable: true });
  }, [updateEligibilityData]);

  // Explicit action: increment click count when user clicks
  const recordClick = useCallback(() => {
    const criteria = currentFeatureEligibilityData?.criteria ?? defaultCriteria;
    const updatedCriteria = incrementClickCount(criteria);
    if (updatedCriteria) {
      updateEligibilityData({ criteria: updatedCriteria });
    }
  }, [currentFeatureEligibilityData, defaultCriteria, updateEligibilityData]);

  // Explicit action: increment dismiss count and record dismiss date
  const recordDismiss = useCallback(() => {
    const criteria = currentFeatureEligibilityData?.criteria ?? defaultCriteria;
    const updatedCriteria = incrementDismissCount(criteria);
    if (updatedCriteria) {
      updateEligibilityData({ criteria: updatedCriteria });
    }
  }, [currentFeatureEligibilityData, defaultCriteria, updateEligibilityData]);

  return {
    shouldShow,
    disableEligibility,
    recordClick,
    recordDismiss,
    eligibilityData: currentFeatureEligibilityData,
  };
};

/**
 * Checks if the criteria type or structure has changed between stored and default.
 * This handles cases where a developer changes the criteria (e.g., from strategy 'all' to 'any')
 * or changes the structure of composite conditions.
 */
const hasCriteriaStructureChanged = (
  stored: EligibilityCriteria | undefined,
  defaultCriteria: EligibilityCriteria
): boolean => {
  if (!stored) return false;

  const storedIsComposite = 'strategy' in stored;
  const defaultIsComposite = 'strategy' in defaultCriteria;

  // One is composite, other is base
  if (storedIsComposite !== defaultIsComposite) {
    return true;
  }

  // Both are base - compare type
  if (!storedIsComposite && !defaultIsComposite) {
    return stored.type !== defaultCriteria.type;
  }

  // Both are composite - compare strategy and condition types
  if (storedIsComposite && defaultIsComposite) {
    if (stored.strategy !== defaultCriteria.strategy) {
      return true;
    }

    const getConditionKeys = (criteria: EligibilityCriteria): string[] => {
      if ('strategy' in criteria) {
        return criteria.conditions.map((c) => ('strategy' in c ? c.strategy : c.type)).sort();
      }
      return [criteria.type];
    };

    const storedConditionKeys = getConditionKeys(stored);
    const defaultConditionKeys = getConditionKeys(defaultCriteria);

    // Different number of conditions
    if (storedConditionKeys.length !== defaultConditionKeys.length) {
      return true;
    }

    // Different condition types
    if (storedConditionKeys.some((key, index) => key !== defaultConditionKeys[index])) {
      return true;
    }
  }

  return false;
};
